/**
 * The home screen's list, as pure functions over what the store already holds.
 *
 * Everything here reads a `StoredSurvey` — the document plus the device's sync
 * bookkeeping — and produces display strings. No engine call is needed for a
 * list row and none is made: opening five surveys' worth of validation to draw
 * five rows would be work the surveyor never asked for.
 */

import type { OutboxOwed, StoredSurvey } from '../../db/store';
import { addressPartsOf, formatAddress } from '../../core/address';
import type { SurveyOrigin, SurveyStatus } from '../../core/types';
import { keyFor } from './results';

export type TagKind = 'assigned' | 'field' | 'submitted';

export interface SurveySummary {
  surveyId: string;
  /** B1, else B2, else a placeholder — never an empty row. */
  title: string;
  origin: SurveyOrigin;
  status: SurveyStatus;
  /**
   * One status word — always one, since the ring's retirement left the row
   * with nothing else to say where a field-initiated survey stood (Task 2,
   * D-034 #7). `In progress` fills the field-initiated/unsubmitted case that
   * used to go untagged; `Assigned` and `Submitted` are unchanged.
   */
  tagLabel: string;
  tagKind: TagKind;
  /** Unsynced local content. Drives nothing on its own; the pill owns sync copy. */
  dirty: boolean;
  submitted: boolean;
  /**
   * For a SUBMITTED row only: what still stands between this phone and the
   * office — "3 items still sending", "1 item needs you" — or `''` once the
   * survey has actually gone (D-052 §2). Printed under the title, and folded
   * into `tagLabel` so the glyph's spoken name says the same thing.
   */
  sendingLine: string;
  /** B7a, ISO `YYYY-MM-DD`, or `''` when never answered. Task 4's due-date facet. */
  dueDate: string;
  /** B10, ISO `YYYY-MM-DD`, or `''` — only asked at all when B8 (consultant bid) is Yes. */
  bidDate: string;
}

function textAnswer(record: StoredSurvey, questionId: string): string {
  const value = record.document.answers[keyFor(questionId)];
  return value?.kind === 'text' ? value.value.trim() : '';
}

/** A `date` answer, or `''` when unanswered — the sibling `textAnswer` never grew. */
function dateAnswer(record: StoredSurvey, questionId: string): string {
  const value = record.document.answers[keyFor(questionId)];
  return value?.kind === 'date' ? value.value : '';
}

export function isSubmitted(status: SurveyStatus): boolean {
  return status === 'submitted_clean' || status === 'submitted_with_gaps';
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/**
 * What a SUBMITTED row still owes the office, in one line — or nothing (D-052 §2).
 *
 * Grand Dunes: the row moved under *Submitted* the instant Chip tapped the
 * button, as the offline-first design requires (submit.ts, step 3 before step
 * 4), and then said nothing more while two photos and the seal behind them sat
 * on the phone all night. "I thought I already submitted it" was true on this
 * screen and false at the office. The row now says which. Parked rows first:
 * they wait for a person, and "still sending" would be a promise about them
 * that never comes true.
 */
export function sendingLineFor(submitted: boolean, owed: OutboxOwed | undefined): string {
  if (!submitted || !owed) return '';
  if (owed.held > 0) return `${plural(owed.held, 'item')} need${owed.held === 1 ? 's' : ''} you`;
  if (owed.sending > 0) return `${plural(owed.sending, 'item')} still sending`;
  return '';
}

export function summarize(record: StoredSurvey, owed?: OutboxOwed): SurveySummary {
  const doc = record.document;
  const name = textAnswer(record, 'B1');
  // The fallback title is the one printed line (D-039): B2 + B2a–c composed,
  // which for a record that holds everything in B2 is B2 exactly.
  const address = formatAddress(addressPartsOf(doc));
  const submitted = isSubmitted(doc.status);
  const sendingLine = sendingLineFor(submitted, owed);

  const summary: SurveySummary = {
    surveyId: doc.surveyId,
    title: name || address || 'Untitled survey',
    origin: doc.origin,
    status: doc.status,
    tagLabel: submitted
      ? sendingLine
        ? `Submitted · ${sendingLine}`
        : 'Submitted'
      : doc.origin === 'field_initiated'
        ? 'In progress'
        : 'Assigned',
    tagKind: submitted ? 'submitted' : doc.origin === 'field_initiated' ? 'field' : 'assigned',
    dirty: record.dirty === 1,
    submitted,
    sendingLine,
    dueDate: dateAnswer(record, 'B7a'),
    bidDate: dateAnswer(record, 'B10'),
  };

  return summary;
}

export interface ChartBucket {
  /** The bucket's identity: `YYYY-MM` for a month, `YYYY` for a year. */
  key: string;
  /** What sits under the bar: "Jan" … "Dec" for a month, "2026" for a year. */
  short: string;
  /** The bar's accessible name and readout: "March 2026" / "2026". */
  label: string;
  count: number;
  /**
   * The bucket `now` falls in — the one bar the card paints amber and the one
   * axis label it sets bold (the compact card, D-043). Exactly one bucket per
   * set carries it: the twelve months always include `now`'s month, and
   * `surveysByYear`'s range always reaches `now`'s year.
   */
  current: boolean;
}

/* Three letters, not one (D-043): "J F M A M J J A S O N D" needed the reader
 * to count from the left to tell June from July, and the compact card has
 * room for "Jan" under every other bar (`SurveyChart.tsx` decides how many
 * to print from their rendered widths). */
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

/** `YYYY-MM` from a `Date`'s LOCAL calendar fields — never `toISOString`,
 * which reads UTC and would shift a late-evening survey onto next month's bar
 * for anyone west of Greenwich. */
function localMonthKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${String(y)}-${m}`;
}

/**
 * The twelve months of `now`'s LOCAL calendar year, January first, each
 * zero-filled. A survey counts on the local month its `createdAt` falls in.
 * `now` is injectable (defaults to the real clock).
 */
export function surveysByMonth(records: readonly StoredSurvey[], now: Date = new Date()): ChartBucket[] {
  const counts = new Map<string, number>();
  for (const record of records) {
    // An unparseable `createdAt` keys this `NaN-NaN` — it is never counted
    // (the loop below only reads the twelve real `YYYY-MM` keys it builds),
    // so the bad record silently drops off the chart instead of corrupting it.
    const key = localMonthKey(new Date(record.document.createdAt));
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const year = now.getFullYear();
  const nowKey = localMonthKey(now);
  const result: ChartBucket[] = [];
  for (let month = 0; month < 12; month++) {
    const key = localMonthKey(new Date(year, month, 1));
    result.push({
      key,
      short: MONTH_SHORT[month] ?? '',
      label: MONTH_LABEL.format(new Date(year, month, 1)),
      count: counts.get(key) ?? 0,
      current: key === nowKey,
    });
  }
  return result;
}

/**
 * One bucket per calendar year from the EARLIEST survey's local year through
 * `now`'s year, ascending, zero-filled between. With no records, exactly one
 * bucket — `now`'s year, count 0 — so the chart always has an axis.
 * A record dated after `now` still counts in its own year (the range grows
 * to include it); nothing is dropped.
 */
export function surveysByYear(records: readonly StoredSurvey[], now: Date = new Date()): ChartBucket[] {
  const counts = new Map<number, number>();
  for (const record of records) {
    const year = new Date(record.document.createdAt).getFullYear();
    counts.set(year, (counts.get(year) ?? 0) + 1);
  }

  // An unparseable `createdAt` lands here as a `NaN` key, and `Math.min`/`max`
  // propagate NaN through the whole range — dropping every bucket, including
  // `now`'s. Filtered out before the range is measured; the bad record still
  // is not counted anywhere (unlike `surveysByMonth`, a `NaN` key here WOULD
  // otherwise have broken the "always has an axis" promise below).
  const nowYear = now.getFullYear();
  const years = [...counts.keys(), nowYear].filter((year) => Number.isFinite(year));
  const earliest = Math.min(...years);
  const latest = Math.max(...years);

  const result: ChartBucket[] = [];
  for (let year = earliest; year <= latest; year++) {
    const label = String(year);
    result.push({ key: label, short: label, label, count: counts.get(year) ?? 0, current: year === nowYear });
  }
  return result;
}

/* ── the chart's other number: the variance (Round 5.1 → D-043) ──────────── */
/*
 * Her ruling (D-043 postscript, 2026-09-06): *"I want to show the % increase
 * from the previous month (and year on the yearly screen) … should reflect
 * the variance %."* So the reading is this calendar period's count against
 * the WHOLE previous period's — this month against all of last month, this
 * year against all of last year — as a percent, always. The compact card's
 * first cut compared the same days of last month (Sep 1–6 against Aug 1–6),
 * which read "+20 from 0" on a phone whose August had been quiet for six
 * days; a like-for-like stretch is fairer arithmetic and a worse reading.
 * `ChartRange` lives here, not in `SurveyChart.tsx`, because both it and
 * this function describe the same axis; the component only imports the type.
 */
export type ChartRange = 'monthly' | 'yearly';

export interface PeriodChange {
  /** Surveys created this calendar period (month or year, local). */
  current: number;
  /** Surveys created in the whole period before it. */
  previous: number;
  /**
   * The variance against `previous`, rounded to a whole percent — or to one
   * decimal when a real change would otherwise round to 0. With nothing in
   * the previous period and something in this one it is 100 (her ruling:
   * a percent always shows); with nothing in either it is 0.
   */
  percent: number;
  direction: 'up' | 'down' | 'flat';
  /** "3 this month" / "64 this year" — the count, with its period named. */
  currentLabel: string;
  /** The variance itself: "+50%", "−25%", "−0.5%"; "No change" when the counts are equal. */
  delta: string;
  /** What it is against, with its count: "vs Aug (5)", "vs Dec 2025 (2)", "vs 2025 (24)". */
  compare: string;
  /** The whole reading in one sentence, for assistive tech: "3 this month, +50% vs Aug (2)". */
  caption: string;
}

const MONTH_ABBR = new Intl.DateTimeFormat('en-US', { month: 'short' });

/**
 * This calendar period's count against the whole previous period's, straight
 * from the records — NOT from `surveysByMonth`'s twelve current-year buckets,
 * because the previous month can fall in last year (December's does) and
 * those buckets never hold it. Local calendar fields throughout, same reason
 * as `localMonthKey`; an unparseable `createdAt` is skipped, counted in
 * neither period.
 */
export function periodChange(
  records: readonly StoredSurvey[],
  range: ChartRange,
  now?: Date,
): PeriodChange {
  const clock = now ?? new Date();
  const curYear = clock.getFullYear();
  const curMonth = clock.getMonth();

  let current = 0;
  let previous = 0;
  let against: string;

  if (range === 'monthly') {
    // `new Date(year, month - 1, 1)` rolls a January backward into last
    // December on its own — the wrap the December case needs, for free.
    const prevMarker = new Date(curYear, curMonth - 1, 1);
    const prevYear = prevMarker.getFullYear();
    const prevMonth = prevMarker.getMonth();

    for (const record of records) {
      const created = new Date(record.document.createdAt);
      if (!Number.isFinite(created.getTime())) continue;
      const y = created.getFullYear();
      const m = created.getMonth();
      if (y === curYear && m === curMonth) current++;
      else if (y === prevYear && m === prevMonth) previous++;
    }

    // The month's name alone while it is this year's; with its year once
    // the wrap has crossed into the last one, so "Dec" cannot be read as a
    // month still to come.
    against = `${MONTH_ABBR.format(prevMarker)}${prevYear === curYear ? '' : ` ${String(prevYear)}`}`;
  } else {
    const prevYear = curYear - 1;
    for (const record of records) {
      const created = new Date(record.document.createdAt);
      if (!Number.isFinite(created.getTime())) continue;
      const y = created.getFullYear();
      if (y === curYear) current++;
      else if (y === prevYear) previous++;
    }
    against = String(prevYear);
  }

  const direction: 'up' | 'down' | 'flat' =
    current === previous ? 'flat' : current > previous ? 'up' : 'down';

  // Her ruling on an empty previous period: a percent still shows, and it is
  // +100% — not the truth of the arithmetic (0 → 20 is not a doubling) but
  // the reading she asked for, and it never prints beside a count that could
  // be mistaken for a second measure.
  let percent: number;
  if (previous === 0) {
    percent = current === 0 ? 0 : 100;
  } else {
    const exact = ((current - previous) / previous) * 100;
    const whole = Math.round(exact);
    // A real change that rounds to a whole 0% (200 → 199 is −0.5%) keeps one
    // decimal instead — "−0%" beside a down arrow would read as no change.
    percent = whole === 0 && exact !== 0 ? Math.round(exact * 10) / 10 || 0 : whole || 0;
  }

  let delta: string;
  if (direction === 'flat') {
    delta = 'No change';
  } else {
    // The sign follows `direction`, never the rounded number's own.
    const sign = direction === 'up' ? '+' : '−';
    const magnitude = Math.abs(percent);
    // Under a tenth of a percent either way is still a change; say so
    // rather than print "+0%".
    delta = magnitude === 0 ? `${sign}<0.1%` : `${sign}${String(magnitude)}%`;
  }

  const currentLabel = `${String(current)} this ${range === 'monthly' ? 'month' : 'year'}`;
  const compare = `vs ${against} (${String(previous)})`;

  return {
    current,
    previous,
    percent,
    direction,
    currentLabel,
    delta,
    compare,
    caption: `${currentLabel}, ${delta} ${compare}`,
  };
}

export interface GroupedSurveys {
  assigned: SurveySummary[];
  field: SurveySummary[];
  submitted: SurveySummary[];
}

/**
 * The mockup's three blocks: work handed to you, work you started, and what has
 * already gone in. Newest first inside each block — a surveyor's next tap is
 * almost always the thing they touched last.
 */
export function groupSurveys(
  records: readonly StoredSurvey[],
  owed: ReadonlyMap<string, OutboxOwed> = new Map(),
): GroupedSurveys {
  const byRecency = [...records].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  const grouped: GroupedSurveys = { assigned: [], field: [], submitted: [] };

  for (const record of byRecency) {
    const summary = summarize(record, owed.get(record.surveyId));
    if (summary.submitted) grouped.submitted.push(summary);
    else if (summary.origin === 'field_initiated') grouped.field.push(summary);
    else grouped.assigned.push(summary);
  }

  return grouped;
}
